import type { BankAccountResponse, FirmGetAllResponse, FirmGetAllTradingAddress, FirmReference } from '@pmp/api';

// A firm as the firm import reads it back: one row per trading address, under the import's column keys
// (FirmImportSchemaFields), in the values it parses. Every row also keeps the list row it comes from, so an export
// of the visible list columns reads from the same rows.
export type FirmExportRow = Record<string, unknown>;

// The import's bank account columns per operation.
const BANK_ACCOUNT_COLUMNS = [
  ['ADVICE', 'advice'],
  ['PACKAGE', 'package'],
  ['NETWORK', 'network'],
  ['MORTGAGE_CLUB', 'mortgageClub'],
] as const;

const LIST_SEPARATOR = ';';

const joined = (values: (string | null | undefined)[] | null | undefined): string =>
  (values ?? []).filter(value => !!value).join(LIST_SEPARATOR);

const ohpenIds = (firms: FirmReference[] | null | undefined): string => joined((firms ?? []).map(firm => firm.ohpenId));

// Address lines 1-4 are LineItems[0..3] and the county is LineItems[4], as the import writes them.
const line = (lineItems: (string | null)[] | null | undefined, index: number): string => lineItems?.[index] ?? '';

const bankAccountColumns = (bankAccounts: Record<string, BankAccountResponse> | undefined): FirmExportRow =>
  Object.fromEntries(
    BANK_ACCOUNT_COLUMNS.flatMap(([operation, prefix]) => {
      const account = bankAccounts?.[operation];
      return [
        [`${prefix}BankAccountName`, account?.accountName ?? ''],
        [`${prefix}BankAccountSortCode`, account?.sortCode ?? ''],
        [`${prefix}BankAccountAccountNumber`, account?.accountNumber ?? ''],
        [`${prefix}BankAccountBankName`, account?.bankName ?? ''],
      ];
    }),
  );

const firmColumns = (firm: FirmGetAllResponse): FirmExportRow => {
  const contact = firm.contactDetails;
  const address = contact?.firmAddress;
  return {
    ohpenId: firm.ohpenId,
    name: firm.name,
    type: firm.type,
    frn: firm.frn ?? '',
    isActive: firm.isActive,
    nacfbNumber: firm.nacfbNumber ?? '',
    reference: firm.reference ?? '',
    qualifiedDomains: joined(contact?.qualifiedDomains),
    btl: firm.btl,
    rbtl: firm.rbtl,
    rmc: firm.rmc,
    operations: joined(firm.operationsWhitelist.map(operation => operation.key)),
    ...bankAccountColumns(firm.bankAccounts),
    principal: firm.principalFirm?.ohpenId ?? '',
    packagers: ohpenIds(firm.packagers),
    paymentRoutes: ohpenIds(firm.paymentRoutes),
    defaultPaymentRoute: firm.defaultPaymentRouteFirmOhpenId ?? '',
    contactEmail: contact?.email ?? '',
    contactMobilePhone: contact?.mobilePhone ?? '',
    contactTelephone: contact?.workPhone ?? '',
    contactWebsite: contact?.website ?? '',
    contactComplaintsWebsite: contact?.complaintsWebsite ?? '',
    addressPostCode: address?.postCode ?? '',
    addressTown: address?.city ?? '',
    addressCounty: line(address?.lineItems, 4),
    addressCountry: address?.country ?? '',
    addressLineOne: line(address?.lineItems, 0),
    addressLineTwo: line(address?.lineItems, 1),
    addressLineThree: line(address?.lineItems, 2),
    addressLineFour: line(address?.lineItems, 3),
  };
};

// A trading address is identified the way the import resolves it: by its OhpenId, or its row id when it has none.
const tradingAddressColumns = (address: FirmGetAllTradingAddress | undefined): FirmExportRow => ({
  tradingAddressId: address ? (address.ohpenId ?? address.id) : '',
  tradingAddressName: address?.name ?? '',
  tradingAddressPostCode: address?.postCode ?? '',
  tradingAddressTown: address?.city ?? '',
  tradingAddressCounty: line(address?.lineItems, 4),
  tradingAddressCountry: address?.country ?? '',
  tradingAddressLineOne: line(address?.lineItems, 0),
  tradingAddressLineTwo: line(address?.lineItems, 1),
  tradingAddressLineThree: line(address?.lineItems, 2),
  tradingAddressLineFour: line(address?.lineItems, 3),
  tradingAddressRegion: address?.region ?? '',
  tradingAddressBdm: address?.businessDevelopmentManager ?? '',
});

/** The export rows of a firm, from its list response and the row the list shows for it. */
export const toExportRows = (firm: FirmGetAllResponse, listRow: FirmExportRow): FirmExportRow[] => {
  const firmRow = { ...listRow, ...firmColumns(firm) };
  const addresses: (FirmGetAllTradingAddress | undefined)[] = firm.tradingAddresses.length ? firm.tradingAddresses : [undefined];
  return addresses.map(address => ({ ...firmRow, ...tradingAddressColumns(address) }));
};
